import cl from 'classnames'
import Image from 'next/image'

import { ArticlePreview } from '@components/article-preview'
import { Divider } from '@components/divider'
import { HeaderSection } from '@components/header-section'
import { SidebarArticlesPreview } from '@components/sidebar-articles-preview'
import styles from './styles.module.scss'
import { Highlight } from '@services/api/highlights'
import { Article } from 'types/article'
import Link from 'next/link'
import { UseMoreReadArticles } from '@hooks/use-more-read-articles'
import { AdsIndexDto } from '@services/api/ads/types'

interface Props {
  className?: string
  highlights: Highlight[]
  lastArticles: Article[]
  ads?: AdsIndexDto[]
}

export const TopNews: React.FC<Props> = ({
  className,
  lastArticles,
  highlights,
  ads = []
}) => {
  const [first, second, third, fourth, fifth, sixth] = highlights
  const { moreReadArticles } = UseMoreReadArticles({
    more_reads: true,
    pagination: { page: 1, per: 6 }
  })
  return (
    <div className={cl('flex flex-col mt-4', className)}>
      <div className="flex flex-col">
        <HeaderSection>
          <h2>Notícias em Destaque</h2>
        </HeaderSection>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 gap-y-4">
          <div className="flex flex-col gap-y-4 lg:gap-y-4 col-span-12 lg:col-span-8 lg:justify-between">
            <div className={styles.mainCards}>
              <ArticlePreview
                articlePath={first?.path}
                imageUrl={first?.thumbnailImage.path}
                direction="top"
                editorial={
                  first?.editorialAscendent
                    ? first?.editorialAscendent.name
                    : first?.editorial.name
                }
                editorialPath={
                  first?.editorialAscendent
                    ? first?.editorialAscendent.path
                    : first?.editorial.path
                }
                title={first?.title}
                description={first?.shortContent}
                readingTime={first ? `${first.timeToRead}` : null}
                publishedAt={first.publishedAt}
                videoUrl={first?.videoUrl}
                articleType={first?.articleType}
              />
              <ArticlePreview
                articlePath={second?.path}
                imageUrl={second?.thumbnailImage.path}
                direction="top"
                editorial={
                  second?.editorialAscendent
                    ? second?.editorialAscendent.name
                    : second?.editorial.name
                }
                editorialPath={
                  second?.editorialAscendent
                    ? second?.editorialAscendent.path
                    : second?.editorial.path
                }
                title={second?.title}
                description={second?.shortContent}
                readingTime={second ? `${second.timeToRead} ` : null}
                publishedAt={second.publishedAt}
                videoUrl={second?.videoUrl}
                articleType={second?.articleType}
              />
            </div>

            <div className="grid grid-cols-1 gap-4">
              {/* third */}
              <a href={third?.path} className="block">
                <div className="grid grid-cols-1 md:grid-cols-3">
                  <Image
                    src={
                      third?.thumbnailImage.path ||
                      '/assets/default-article.jpg'
                    }
                    alt={third?.title || 'Imagem da notícia'}
                    width={300}
                    height={200}
                    className="object-cover rounded-lg w-full"
                  />
                  <div className="md:col-span-2 flex flex-col md:ps-4 mt-2 md:mt-0 mb-2 relative">
                    <span className="bg-green w-fit text-white px-3 py-1 rounded text-sm font-medium mr-2 mb-2">
                      {third?.editorial?.name || 'NOTÍCIA'}
                    </span>
                    <h3 className="font-bold text-lg mb-2 h-14 overflow-hidden">
                      {third?.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                      {third?.shortContent ||
                        'Conteúdo da notícia em destaque.'}
                    </p>
                    <p className="text-xs text-gray-500 mt-auto flex justify-between w-full absolute bottom-0">
                      <span>
                        {new Date(third?.publishedAt).toLocaleDateString(
                          'pt-BR'
                        )}
                      </span>
                      <span className="pe-4">
                        {third?.timeToRead} min de leitura
                      </span>
                    </p>
                  </div>
                </div>
              </a>
              {/* fourth */}
              <a href={fourth?.path} className="block">
                <div className="grid grid-cols-1 md:grid-cols-3">
                  <Image
                    src={
                      fourth?.thumbnailImage.path ||
                      '/assets/default-article.jpg'
                    }
                    alt={fourth?.title || 'Imagem da notícia'}
                    width={300}
                    height={200}
                    className="object-cover rounded-lg w-full"
                  />
                  <div className="md:col-span-2 flex flex-col md:ps-4 mt-2 md:mt-0 mb-2 relative">
                    <span className="bg-green w-fit text-white px-3 py-1 rounded text-sm font-medium mr-2 mb-2">
                      {fourth?.editorial?.name || 'NOTÍCIA'}
                    </span>
                    <h3 className="font-bold text-lg mb-2 h-14 overflow-hidden">
                      {fourth?.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                      {fourth?.shortContent ||
                        'Conteúdo da notícia em destaque.'}
                    </p>
                    <p className="text-xs text-gray-500 mt-auto flex justify-between w-full absolute bottom-0">
                      <span>
                        {new Date(fourth?.publishedAt).toLocaleDateString(
                          'pt-BR'
                        )}
                      </span>
                      <span className="pe-4">
                        {fourth?.timeToRead} min de leitura
                      </span>
                    </p>
                  </div>
                </div>
              </a>
              {/* fifth */}
              {fifth && (
                <a href={fifth?.path} className="block">
                  <div className="grid grid-cols-1 md:grid-cols-3">
                    <Image
                      src={
                        fifth?.thumbnailImage.path ||
                        '/assets/default-article.jpg'
                      }
                      alt={fifth?.title || 'Imagem da notícia'}
                      width={300}
                      height={200}
                      className="object-cover rounded-lg w-full"
                    />
                    <div className="md:col-span-2 flex flex-col md:ps-4 mt-2 md:mt-0 mb-2 relative">
                      <span className="bg-green w-fit text-white px-3 py-1 rounded text-sm font-medium mr-2 mb-2">
                        {fifth?.editorial?.name || 'NOTÍCIA'}
                      </span>
                      <h3 className="font-bold text-lg mb-2 h-14 overflow-hidden">
                        {fifth?.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                        {fifth?.shortContent ||
                          'Conteúdo da notícia em destaque.'}
                      </p>
                      <p className="text-xs text-gray-500 mt-auto flex justify-between w-full absolute bottom-0">
                        <span>
                          {new Date(fifth?.publishedAt).toLocaleDateString(
                            'pt-BR'
                          )}
                        </span>
                        <span className="pe-4">
                          {fifth?.timeToRead} min de leitura
                        </span>
                      </p>
                    </div>
                  </div>
                </a>
              )}
              {/* sixth */}
              {sixth && (
                <a href={sixth?.path} className="block">
                  <div className="grid grid-cols-1 md:grid-cols-3">
                    <Image
                      src={
                        sixth?.thumbnailImage.path ||
                        '/assets/default-article.jpg'
                      }
                      alt={sixth?.title || 'Imagem da notícia'}
                      width={300}
                      height={200}
                      className="object-cover rounded-lg w-full"
                    />
                    <div className="md:col-span-2 flex flex-col md:ps-4 mt-2 md:mt-0 mb-2 relative">
                      <span className="bg-green w-fit text-white px-3 py-1 rounded text-sm font-medium mr-2 mb-2">
                        {sixth?.editorial?.name || 'NOTÍCIA'}
                      </span>
                      <h3 className="font-bold text-lg mb-2 h-14 overflow-hidden">
                        {sixth?.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                        {sixth?.shortContent ||
                          'Conteúdo da notícia em destaque.'}
                      </p>
                      <p className="text-xs text-gray-500 mt-auto flex justify-between w-full absolute bottom-0">
                        <span>
                          {new Date(sixth?.publishedAt).toLocaleDateString(
                            'pt-BR'
                          )}
                        </span>
                        <span className="pe-4">
                          {sixth?.timeToRead} min de leitura
                        </span>
                      </p>
                    </div>
                  </div>
                </a>
              )}
            </div>
          </div>
          <div className="flex flex-col col-span-12 lg:col-span-4">
            <div className="flex flex-row gap-x-4">
              <Divider
                type="secondary"
                orientation="vertical"
                className="hidden lg:block"
              />

              <SidebarArticlesPreview
                className="w-full"
                articles={lastArticles}
                showMoreLink={'/noticias/ultimas-noticias'}
                articlesSecondaries={moreReadArticles}
                ads={ads}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
